import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import { api } from '../services/api';
import { useNotification } from '../Notifications';

import { Check, Info, AlertTriangle, ChevronRight, Power, Circle, ChevronDown, Maximize2, Database, Eye, Moon, Trash2, Camera, CreditCard, Plus, Server, Key, Folder, Image, LogOut } from 'lucide-react';
import CreateVolumeModal from '../components/Volumes/CreateVolumeModal';
import { useResponsive } from '../components/vm/useResponsive';
import { 
  sanitizeText, 
  validateSnapshotName, 
  logUserAction, 
  logInfo,
  logError,
  logPerformance,
  currencyUtils,
  getNetworkErrorMessage,  // ADD THIS
  getSafeErrorMessage,     // ADD THIS
  ERROR_MESSAGES           // ADD THIS
} from './dashboardUtils';

// Helper function to detect advanced regions
const isAdvancedRegion = (vm) => {
  const advancedRegions = [
    "FINLAND-1", "FINLAND-2", "FINLAND-3",
    "FIN-01", "FIN-02", "FIN-03", "ICE-01"
  ];
  return advancedRegions.includes(vm?.region);
};

// Helper function for consistent status text color using #cad9e3
const getStatusTextColor = (status) => {
  return 'bg-[rgba(202,217,227,0.1)] text-[#cad9e3] border-[rgba(202,217,227,0.3)]';
};

// ============================================================================
// SNAPSHOT CONFIRMATION MODAL - MATCHING CreateSnapshotModal LAYOUT
// ============================================================================

// ClassyToggle Component for is_image toggle
const ClassyToggle = ({ checked, onChange, disabled, label, id }) => {
  return (
    <div className="flex items-center space-x-3">
      <div className="relative">
        <input
          type="checkbox"
          id={id}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="sr-only"
        />
        <label
          htmlFor={id}
          className={`
            flex items-center cursor-pointer select-none
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <div className={`
            relative inline-flex h-6 w-11 items-center justify-center rounded-full transition-all duration-300 ease-in-out
            ${checked 
              ? 'bg-gradient-to-r from-[#e0eacc] to-[#c8d9a9] shadow-lg shadow-[#e0eacc]/25' 
              : 'bg-gradient-to-r from-[#2a2a2a] to-[#1a1a1a] shadow-inner'
            }
            ${!disabled && 'hover:shadow-xl'}
            border border-opacity-20
            ${checked ? 'border-[#e0eacc]' : 'border-[#444444]'}
          `}>
            {checked && (
              <Check className="text-white w-3 h-3 drop-shadow-sm z-10" />
            )}
            
            {checked && (
              <div className="absolute inset-0 rounded-full animate-ping bg-[#e0eacc] opacity-20" />
            )}
          </div>
        </label>
      </div>
      
      <label 
        htmlFor={id} 
        className={`
          text-sm font-medium transition-colors duration-200 cursor-pointer select-none
          ${checked ? 'text-[#e0eacc]' : 'text-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:text-white'}
        `}
      >
        {label}
      </label>
    </div>
  );
};
// ============================================================================
// SNAPSHOT CONFIRMATION MODAL - MATCHING CreateSnapshotModal LAYOUT
// ============================================================================

// Snapshot Icon Component
const SnapshotIcon = ({ size = 'medium' }) => {
  let width, height;
  
  if (size === 'large') {
    width = '64px';
    height = '64px';
  } else if (size === 'medium') {
    width = '40px';
    height = '40px';
  } else if (size === 'small') {
    width = '24px';
    height = '24px';
  } else {
    width = '40px';
    height = '40px';
  }
  
  return (
    <div style={{ width, height, position: 'relative', flexShrink: 0 }}>
      <svg viewBox="0 0 24 24" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        <rect
          x="2" y="4" width="20" height="16" rx="2"
          fill="#1b2b1a"
          stroke="#e0eacc"
          strokeWidth="1"
        />
        <path
          d="M6 8 L18 8 L18 16 L6 16 Z"
          fill="#e0eacc"
          fillOpacity="0.2"
        />
        <circle
          cx="12" cy="12" r="3"
          fill="#e0eacc"
        />
        <circle
          cx="15" cy="9" r="1"
          fill="#e0eacc"
        />
      </svg>
    </div>
  );
};

// Inline styles generator for desktop layout
const getSnapshotModalStyles = (isSubmitting) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '680px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: '24px',
    borderRight: '1px solid #2a2a2a',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  rightPanel: {
    width: '260px',
    padding: '24px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  iconWrapper: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    background: isSubmitting 
      ? 'linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%)'
      : 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(224,234,204,0.3)',
    transition: 'all 0.15s',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '12px',
  },
  label: {
    color: '#9ca3af',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '6px',
    display: 'block',
  },
  required: {
    color: '#ff6b6b',
    marginLeft: '2px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
    minHeight: '60px',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: '11px',
    marginTop: '4px',
  },
  submitBtn: {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: isSubmitting ? '#3d4435' : '#e0eacc',
    color: isSubmitting ? '#a0a89a' : 'black',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(224,234,204,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '48px',
  },
  cancelBtn: {
    width: '100%',
    marginTop: '10px',
    padding: '12px 24px',
    backgroundColor: '#333',
    color: isSubmitting ? '#666' : 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    opacity: isSubmitting ? 0.5 : 1,
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(0,0,0,0.2)',
    borderTopColor: 'black',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '12px',
    margin: '0 0 4px 0',
  },
  summaryValue: {
    color: 'white',
    fontSize: '15px',
    margin: 0,
    wordBreak: 'break-all',
  },
  advancedBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#e0eacc',
    fontSize: '13px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px 0',
    transition: 'color 0.15s',
  },
});

// Main SnapshotConfirmationModal Component
// ============================================================================
// SNAPSHOT CONFIRMATION MODAL - FIXED: Validation on submit only, inline errors
// ============================================================================

const SnapshotConfirmationModal = ({ isOpen, onClose, onConfirm, vm, isCreating, existingSnapshotNames = [] }) => {
  const { isMobile } = useResponsive();

  const [snapshotName, setSnapshotName] = useState('');
  const [description, setDescription] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameCharCount, setNameCharCount] = useState(0);

  const isSubmitting = isCreating;
  const styles = getSnapshotModalStyles(isSubmitting);

  // Component lifecycle logging
  useEffect(() => {
    if (isOpen && vm) {
      logInfo('SnapshotConfirmationModal opened', {
        vmId: vm.id,
        vmName: vm.name,
        vmStatus: vm.status,
        existingSnapshotsCount: existingSnapshotNames.length
      });
    }

    return () => {
      if (isOpen && vm) {
        logInfo('SnapshotConfirmationModal closed', {
          vmId: vm.id,
          snapshotNameEntered: snapshotName.trim().length > 0
        });
      }
    };
  }, [isOpen, vm]);

  // VM validation logic - TWO LAYER PROTECTION
  const canTakeSnapshot = useMemo(() => {
    if (!vm) return false;
    const isActive = vm.status === 'RUNNING';
    const isLegacyRegion = !isAdvancedRegion(vm);
    const result = isActive && isLegacyRegion;
    
    logInfo('Snapshot capability check', {
      vmId: vm?.id,
      vmName: vm?.name,
      vmStatus: vm?.status,
      isActive,
      isLegacyRegion,
      canTakeSnapshot: result
    });
    
    return result;
  }, [vm]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && vm) {
      const defaultName = `${vm.name}-snapshot`;
      setSnapshotName(defaultName);
      setNameCharCount(defaultName.length);
      setDescription('');
      setShowAdvanced(false);
      setNameError('');
      
      logInfo('Snapshot form initialized', {
        vmId: vm.id,
        defaultName,
        nameLength: defaultName.length
      });
    }
  }, [isOpen, vm]);

  // Handle name change - NO real-time validation, just clear previous error
  const handleNameChange = useCallback((e) => {
    const value = e.target.value;
    setSnapshotName(value);
    setNameCharCount(value.length);
    
    // Clear any previous error when user starts typing again
    if (nameError) {
      setNameError('');
    }
  }, [nameError]);

  // Handle description change
  const handleDescriptionChange = useCallback((e) => {
    const value = e.target.value;
    setDescription(value);
    
    logUserAction('snapshot_description_change', {
      descriptionLength: value.length,
      vmId: vm?.id
    });
  }, [vm]);

  // Handle advanced options toggle
  const handleShowAdvancedToggle = useCallback(() => {
    const newState = !showAdvanced;
    setShowAdvanced(newState);
    
    logUserAction('snapshot_advanced_options_toggle', {
      showAdvanced: newState,
      vmId: vm?.id
    });
  }, [showAdvanced, vm]);

  // Handle form submission - Validation happens HERE only
  const handleSubmit = useCallback(() => {
    const startTime = performance.now();
    
    // TWO LAYER PROTECTION - Check VM state again
    if (!canTakeSnapshot) {
      logError('Snapshot creation blocked - VM validation failed', {
        vmId: vm?.id,
        vmName: vm?.name,
        vmStatus: vm?.status,
        isAdvancedRegion: isAdvancedRegion(vm)
      });
      return;
    }
    
    const trimmedName = snapshotName.trim();
    
    // Validate snapshot name
    const validation = validateSnapshotName(trimmedName);
    if (!validation.valid) {
      setNameError(validation.error);
      
      logError('Snapshot creation blocked - name validation failed', {
        vmId: vm?.id,
        vmName: vm?.name,
        snapshotName: trimmedName,
        validationError: validation.error
      });
      return;
    }
    
    // Check for duplicate names
    if (existingSnapshotNames.includes(trimmedName)) {
      const duplicateError = 'A snapshot with this name already exists';
      setNameError(duplicateError);
      
      logError('Snapshot creation blocked - duplicate name', {
        vmId: vm?.id,
        vmName: vm?.name,
        duplicateName: trimmedName
      });
      return;
    }
    
    // All validation passed - clear error and proceed
    setNameError('');
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    logPerformance('snapshot_validation', duration, {
      vmId: vm?.id,
      vmName: vm?.name,
      snapshotName: trimmedName,
      validationPassed: true
    });
    
    logUserAction('snapshot_creation_confirmed', {
      vmId: vm?.id,
      vmName: vm?.name,
      snapshotName: trimmedName,
      hasDescription: !!description.trim(),
      validationDuration: `${duration.toFixed(2)}ms`
    });
    
    // Call onConfirm with snapshot data object
    const snapshotData = {
      name: sanitizeText(trimmedName),
      description: sanitizeText(description.trim())
    };
    
    onConfirm(snapshotData);
  }, [snapshotName, description, onConfirm, existingSnapshotNames, canTakeSnapshot, vm]);

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isCreating) {
      logUserAction('snapshot_modal_closed', {
        vmId: vm?.id,
        vmName: vm?.name,
        snapshotNameEntered: snapshotName.trim().length > 0,
        wasCancelled: true
      });
      onClose();
    }
  }, [onClose, isCreating, vm, snapshotName]);

  if (!isOpen) return null;

  // MOBILE LAYOUT
  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
            .custom-scrollbar {
              scrollbar-width: thin;
              scrollbar-color: #555555 #2a2a2a;
            }
            .custom-scrollbar::-webkit-scrollbar {
              width: 6px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #2a2a2a;
              border-radius: 3px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #555555;
              border-radius: 3px;
            }
          `}
        </style>
        <div className="bg-[#121212] rounded-lg border border-[#333333] shadow-xl overflow-hidden w-full max-w-sm max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center border-b border-[#222222] flex-shrink-0 p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-[#1a1a1a] mr-2">
                <SnapshotIcon size="small" />
              </div>
              <h2 className="text-lg font-semibold text-white">Create Snapshot</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-[#1a1a1a] text-gray-400 hover:text-white transition-colors"
              disabled={isCreating}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            <div className="p-4 pb-8">
              {/* VM Info - Read Only */}
              <div className="mb-4 p-3 bg-[#1a1a1a] rounded-lg border border-[#333333]">
                <p className="text-gray-400 text-xs mb-1">Source VM</p>
                <p className="text-white font-medium text-sm">{sanitizeText(vm?.name)}</p>
                <p className="text-gray-500 text-xs mt-1">{sanitizeText(vm?.region)}</p>
              </div>
              
              {/* Snapshot Name Field */}
              <div className="mb-4">
                <div className="flex justify-between mb-2">
                  <label htmlFor="name" className="text-gray-300 font-medium text-sm">
                    Snapshot Name <span className="text-[#ff8080]">*</span>
                  </label>
                  <span className={`text-xs ${nameCharCount > 25 ? 'text-[#ff8080]' : 'text-gray-500'}`}>
                    {nameCharCount}/30
                  </span>
                </div>
                <input
                  type="text"
                  id="name"
                  value={snapshotName}
                  onChange={handleNameChange}
                  className={`w-full border ${nameError ? 'border-[#ff8080]' : 'border-[#333333]'} rounded text-white`}
                  style={{
                    outline: 'none',
                    backgroundColor: '#1d1d1d',
                    height: '44px',
                    fontSize: '14px',
                    padding: '0 12px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = nameError ? '#ff8080' : '#e0eacc'}
                  onBlur={(e) => e.target.style.borderColor = nameError ? '#ff8080' : '#333333'}
                  placeholder="Snapshot name"
                  maxLength={30}
                  disabled={isCreating || !canTakeSnapshot}
                  required
                />
                {nameError ? (
                  <p className="mt-1 text-[#ff8080] text-xs">{nameError}</p>
                ) : (
                  <p className="mt-1 text-gray-500 text-xs">Choose a unique name for your snapshot</p>
                )}
              </div>
              
              {/* Advanced Options Toggle */}
              <div className="mb-4">
                <button
                  type="button"
                  onClick={handleShowAdvancedToggle}
                  className="text-[#e0eacc] hover:text-[#c8d9a9] text-sm focus:outline-none flex items-center"
                  disabled={isCreating}
                >
                  <Info className="mr-2 w-4 h-4" />
                  {showAdvanced ? 'Hide advanced options' : 'Show advanced options'}
                </button>
              </div>
              
              {/* Advanced Options - Description Only */}
              {showAdvanced && (
                <div className="mb-4">
                  <div className="mb-2">
                    <label htmlFor="description" className="text-gray-300 font-medium text-sm">
                      Description (optional)
                    </label>
                  </div>
                  <textarea
                    id="description"
                    value={description}
                    onChange={handleDescriptionChange}
                    className="w-full border border-[#333333] rounded text-white"
                    style={{
                      outline: 'none',
                      backgroundColor: '#1d1d1d',
                      fontSize: '14px',
                      padding: '12px',
                      minHeight: '80px',
                      resize: 'vertical'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                    onBlur={(e) => e.target.style.borderColor = '#333333'}
                    placeholder="Enter snapshot description"
                    disabled={isCreating}
                  />
                  <p className="mt-1 text-gray-500 text-xs">
                    Optional description to help identify this snapshot
                  </p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-col space-y-3 mt-8">
                <button
                  onClick={handleSubmit}
                  disabled={isCreating || !snapshotName.trim() || !canTakeSnapshot}
                  className={`w-full flex items-center justify-center py-3 px-4 text-sm rounded font-medium transition-colors ${
                    isCreating || !snapshotName.trim() || !canTakeSnapshot
                      ? 'bg-gray-700 cursor-not-allowed text-gray-400'
                      : 'bg-[#e0eacc] hover:bg-[#c8d9a9] text-black'
                  }`}
                  style={{ minHeight: '44px' }}
                  title={!canTakeSnapshot ? 'VM must be RUNNING and in legacy region for snapshots' : ''}
                >
                  {isCreating ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-t-black border-black border-opacity-20 rounded-full mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 w-4 h-4" />
                      Create Snapshot
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleClose}
                  className="w-full bg-transparent hover:bg-[rgba(202,217,227,0.1)] text-[#cad9e3] py-3 px-4 rounded border border-[#cad9e3] transition-colors text-sm font-medium"
                  disabled={isCreating}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // DESKTOP LAYOUT - Two Panel Design
  return (
    <>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #555555 #2a2a2a;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #2a2a2a;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #555555;
            border-radius: 3px;
          }
        `}
      </style>
      
      <div style={styles.overlay}>
        <div style={styles.modal}>
          {/* LEFT PANEL - Configuration */}
          <div style={styles.leftPanel}>
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.iconWrapper}>
                <Camera size={18} style={{ color: isSubmitting ? '#666' : '#1a1a1a' }} />
              </div>
              <div>
                <h2 style={styles.title}>Create Snapshot</h2>
                <p style={styles.subtitle}>Capture VM state for backup or cloning</p>
              </div>
            </div>

            {/* VM Info - Read Only Display */}
            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>Source Virtual Machine</label>
              <div style={{
                padding: '12px 14px',
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '8px',
              }}>
                <div style={{ fontSize: '14px', color: 'white', marginBottom: '2px' }}>
                  {sanitizeText(vm?.name)}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                  {sanitizeText(vm?.region)}
                </div>
              </div>
            </div>

            {/* Snapshot Name */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <label style={styles.label}>
                  Snapshot Name<span style={styles.required}>*</span>
                </label>
                <span style={{ fontSize: '11px', color: nameCharCount > 25 ? '#ff6b6b' : '#6b7280' }}>
                  {nameCharCount}/30
                </span>
              </div>
              <input
                type="text"
                value={snapshotName}
                onChange={handleNameChange}
                placeholder="Enter snapshot name"
                disabled={isSubmitting || !canTakeSnapshot}
                maxLength={30}
                style={{
                  ...styles.input,
                  borderColor: nameError ? '#ff6b6b' : '#333',
                }}
                onFocus={(e) => !isSubmitting && (e.target.style.borderColor = nameError ? '#ff6b6b' : '#e0eacc')}
                onBlur={(e) => e.target.style.borderColor = nameError ? '#ff6b6b' : '#333'}
              />
              {nameError && <div style={styles.errorText}>{nameError}</div>}
            </div>

            {/* Advanced Options Toggle */}
            <div style={{ marginBottom: '14px' }}>
              <button
                type="button"
                onClick={handleShowAdvancedToggle}
                disabled={isSubmitting}
                style={styles.advancedBtn}
                onMouseEnter={(e) => !isSubmitting && (e.target.style.color = '#c8d9a9')}
                onMouseLeave={(e) => e.target.style.color = '#e0eacc'}
              >
                <Info size={14} />
                {showAdvanced ? 'Hide description' : 'Add description'}
              </button>
            </div>

            {/* Advanced Options - Description */}
            {showAdvanced && (
              <div style={{ marginBottom: '0' }}>
                <label style={styles.label}>Description (optional)</label>
                <textarea
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="Enter snapshot description"
                  disabled={isSubmitting}
                  style={styles.textarea}
                  onFocus={(e) => !isSubmitting && (e.target.style.borderColor = '#e0eacc')}
                  onBlur={(e) => e.target.style.borderColor = '#333'}
                />
              </div>
            )}
          </div>

          {/* RIGHT PANEL - Summary */}
          <div style={styles.rightPanel}>
            <h3 style={{
              color: '#6b7280',
              fontSize: '11px',
              letterSpacing: '1.5px',
              marginTop: 0,
              marginBottom: '20px',
            }}>SUMMARY</h3>
            
            {/* Source VM */}
            <div style={{ marginBottom: '16px' }}>
              <p style={styles.summaryLabel}>Source VM</p>
              <p style={styles.summaryValue}>{sanitizeText(vm?.name)}</p>
            </div>

            {/* Region */}
            <div style={{ marginBottom: '16px' }}>
              <p style={styles.summaryLabel}>Region</p>
              <p style={{ ...styles.summaryValue, fontSize: '13.5px' }}>{sanitizeText(vm?.region)}</p>
            </div>

            {/* VM Status */}
            <div style={{ marginBottom: '16px' }}>
              <p style={styles.summaryLabel}>VM Status</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: vm?.status === 'RUNNING' ? '#22c55e' : '#6b7280',
                }} />
                <p style={{ ...styles.summaryValue, fontSize: '13.5px' }}>{vm?.status || 'Unknown'}</p>
              </div>
            </div>

            {/* Snapshot Name Preview */}
            {snapshotName && (
              <div style={{ marginBottom: '16px' }}>
                <p style={styles.summaryLabel}>Snapshot Name</p>
                <p style={styles.summaryValue}>{sanitizeText(snapshotName)}</p>
              </div>
            )}

            <div style={{ borderTop: '1px solid #222', paddingTop: '20px', marginTop: 'auto' }}></div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !snapshotName.trim() || !canTakeSnapshot}
              style={{
                ...styles.submitBtn,
                opacity: (isSubmitting || !snapshotName.trim() || !canTakeSnapshot) && !isSubmitting ? 0.5 : 1,
              }}
              title={!canTakeSnapshot ? 'VM must be RUNNING and in legacy region for snapshots' : ''}
            >
              {isSubmitting ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={styles.spinner}></div>
                  <span>Creating...</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Check size={14} />
                  <span>Create Snapshot</span>
                </div>
              )}
            </button>
            
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              style={styles.cancelBtn}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
const HibernateConfirmationModal = ({ isOpen, onClose, onConfirm, vm, isHibernating }) => {
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (isOpen && vm) {
      logInfo('HibernateConfirmationModal opened', {
        vmId: vm.id,
        vmName: vm.name,
        vmStatus: vm.status,
        isAdvancedRegion: isAdvancedRegion(vm)
      });
    }

    return () => {
      if (isOpen && vm) {
        logInfo('HibernateConfirmationModal closed', {
          vmId: vm.id,
          wasConfirmed: false
        });
      }
    };
  }, [isOpen, vm]);

  const canHibernate = useMemo(() => {
    if (!vm) return false;
    const isAdvanced = isAdvancedRegion(vm);
    const result = isAdvanced 
      ? ['RUNNING', 'SHUTOFF'].includes(vm.status)
      : ['RUNNING', 'SHUTOFF'].includes(vm.status);
      
    logInfo('Hibernate capability check', {
      vmId: vm?.id,
      vmName: vm?.name,
      vmStatus: vm?.status,
      isAdvanced,
      canHibernate: result
    });
    
    return result;
  }, [vm]);

  const handleConfirm = useCallback(() => {
    logUserAction('hibernate_confirmed', {
      vmId: vm?.id,
      vmName: vm?.name,
      vmStatus: vm?.status
    });
    
    onConfirm();
  }, [onConfirm, vm]);

  const handleClose = useCallback(() => {
    logUserAction('hibernate_modal_cancelled', {
      vmId: vm?.id,
      vmName: vm?.name
    });
    
    onClose();
  }, [onClose, vm]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#121212',
        border: '1px solid #333333',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #2a2a2a'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Moon Icon */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#e0eacc">
              <path d="M12.43 2.3c-2.38-.59-4.68-.27-6.63.64-.35.16-.41.64-.1.86C8.3 5.6 10 8.6 10 12c0 3.4-1.7 6.4-4.3 8.2-.32.22-.26.7.09.86 1.28.6 2.71.94 4.21.94 6.05 0 10.85-5.38 9.87-11.6-.61-3.92-3.59-7.16-7.44-8.1z"/>
            </svg>
            <h3 style={{
              fontWeight: '600',
              color: '#fff',
              margin: 0,
              fontSize: '16px'
            }}>
              Hibernate VM
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isHibernating}
            style={{
              padding: '8px',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: isHibernating ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <p style={{
            color: '#9ca3af',
            marginBottom: '16px',
            marginTop: 0,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            Are you sure you want to hibernate this virtual machine?
          </p>
          
          {/* VM Name Box */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '16px'
          }}>
            <span style={{
              color: '#6b7280',
              fontSize: '11px',
              display: 'block',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              VM Name
            </span>
            <span style={{
              color: '#e0eacc',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {sanitizeText(vm?.name || 'Unknown')}
            </span>
          </div>
          
          {/* Info Box - Green Theme */}
          <div style={{
            backgroundColor: 'rgba(224,234,204,0.05)',
            border: '1px solid rgba(224,234,204,0.2)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#e0eacc" style={{ flexShrink: 0 }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            <span style={{ color: '#e0eacc', fontSize: '12px' }}>
              Billing will pause while the VM is hibernated.
            </span>
          </div>
          
          {/* Hibernate Button - Green Theme */}
          <button
            onClick={handleConfirm}
            disabled={!canHibernate || isHibernating}
            style={{
              width: '100%',
              backgroundColor: (!canHibernate || isHibernating) ? '#3d4435' : '#e0eacc',
              border: 'none',
              color: (!canHibernate || isHibernating) ? '#888' : '#000',
              borderRadius: '8px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 20px',
              fontSize: '14px',
              cursor: (!canHibernate || isHibernating) ? 'not-allowed' : 'pointer',
              gap: '8px'
            }}
          >
            {isHibernating ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTopColor: '#000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Hibernating...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.43 2.3c-2.38-.59-4.68-.27-6.63.64-.35.16-.41.64-.1.86C8.3 5.6 10 8.6 10 12c0 3.4-1.7 6.4-4.3 8.2-.32.22-.26.7.09.86 1.28.6 2.71.94 4.21.94 6.05 0 10.85-5.38 9.87-11.6-.61-3.92-3.59-7.16-7.44-8.1z"/>
                </svg>
                Hibernate VM
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};// UPDATED: Reboot Confirmation Modal Component with comprehensive logging
const RebootConfirmationModal = ({ isOpen, onClose, onConfirm, vm, isRebooting }) => {
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (isOpen && vm) {
      logInfo('RebootConfirmationModal opened', {
        vmId: vm.id,
        vmName: vm.name,
        vmStatus: vm.status,
        isAdvancedRegion: isAdvancedRegion(vm)
      });
    }

    return () => {
      if (isOpen && vm) {
        logInfo('RebootConfirmationModal closed', {
          vmId: vm.id,
          wasConfirmed: false
        });
      }
    };
  }, [isOpen, vm]);

  const canReboot = useMemo(() => {
    if (!vm) return false;
    const isAdvanced = isAdvancedRegion(vm);
    const result = !isAdvanced && vm.status === 'RUNNING';
    
    logInfo('Reboot capability check', {
      vmId: vm?.id,
      vmName: vm?.name,
      vmStatus: vm?.status,
      isAdvanced,
      canReboot: result
    });
    
    return result;
  }, [vm]);

  const handleConfirm = useCallback(() => {
    logUserAction('reboot_confirmed', {
      vmId: vm?.id,
      vmName: vm?.name,
      vmStatus: vm?.status
    });
    
    onConfirm();
  }, [onConfirm, vm]);

  const handleClose = useCallback(() => {
    logUserAction('reboot_modal_cancelled', {
      vmId: vm?.id,
      vmName: vm?.name
    });
    
    onClose();
  }, [onClose, vm]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#121212',
        border: '1px solid #333333',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #2a2a2a'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Refresh Icon */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#e0eacc">
              <path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            <h3 style={{
              fontWeight: '600',
              color: '#fff',
              margin: 0,
              fontSize: '16px'
            }}>
              Reboot VM
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isRebooting}
            style={{
              padding: '8px',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: isRebooting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <p style={{
            color: '#9ca3af',
            marginBottom: '16px',
            marginTop: 0,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            Are you sure you want to reboot this virtual machine?
          </p>
          
          {/* VM Name Box */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '16px'
          }}>
            <span style={{
              color: '#6b7280',
              fontSize: '11px',
              display: 'block',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              VM Name
            </span>
            <span style={{
              color: '#e0eacc',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {sanitizeText(vm?.name || 'Unknown')}
            </span>
          </div>
          
          {/* Info Box - Green Theme */}
          <div style={{
            backgroundColor: 'rgba(224,234,204,0.05)',
            border: '1px solid rgba(224,234,204,0.2)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#e0eacc" style={{ flexShrink: 0 }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            <span style={{ color: '#e0eacc', fontSize: '12px' }}>
              The VM will restart and temporarily be unavailable.
            </span>
          </div>
          
          {/* Reboot Button - Green Theme */}
          <button
            onClick={handleConfirm}
            disabled={!canReboot || isRebooting}
            style={{
              width: '100%',
              backgroundColor: (!canReboot || isRebooting) ? '#3d4435' : '#e0eacc',
              border: 'none',
              color: (!canReboot || isRebooting) ? '#888' : '#000',
              borderRadius: '8px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 20px',
              fontSize: '14px',
              cursor: (!canReboot || isRebooting) ? 'not-allowed' : 'pointer',
              gap: '8px'
            }}
          >
            {isRebooting ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTopColor: '#000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Rebooting...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                </svg>
                Reboot VM
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const StartConfirmationModal = ({ isOpen, onClose, onConfirm, vm, isStarting }) => {
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (isOpen && vm) {
      logInfo('StartConfirmationModal opened', {
        vmId: vm.id,
        vmName: vm.name,
        vmStatus: vm.status
      });
    }

    return () => {
      if (isOpen && vm) {
        logInfo('StartConfirmationModal closed', {
          vmId: vm.id,
          wasConfirmed: false
        });
      }
    };
  }, [isOpen, vm]);

  const canStart = useMemo(() => {
    if (!vm) return false;
    const result = vm.status === 'SHUTOFF';
    
    logInfo('Start capability check', {
      vmId: vm?.id,
      vmName: vm?.name,
      vmStatus: vm?.status,
      canStart: result
    });
    
    return result;
  }, [vm]);

  const handleConfirm = useCallback(() => {
    logUserAction('start_confirmed', {
      vmId: vm?.id,
      vmName: vm?.name,
      vmStatus: vm?.status
    });
    
    onConfirm();
  }, [onConfirm, vm]);

  const handleClose = useCallback(() => {
    logUserAction('start_modal_cancelled', {
      vmId: vm?.id,
      vmName: vm?.name
    });
    
    onClose();
  }, [onClose, vm]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#121212',
        border: '1px solid #333333',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #2a2a2a'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Play Icon */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#e0eacc">
              <path d="M8 5v14l11-7z"/>
            </svg>
            <h3 style={{
              fontWeight: '600',
              color: '#fff',
              margin: 0,
              fontSize: '16px'
            }}>
              Start VM
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isStarting}
            style={{
              padding: '8px',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: isStarting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <p style={{
            color: '#9ca3af',
            marginBottom: '16px',
            marginTop: 0,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            Are you sure you want to start this virtual machine?
          </p>
          
          {/* VM Name Box */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '16px'
          }}>
            <span style={{
              color: '#6b7280',
              fontSize: '11px',
              display: 'block',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              VM Name
            </span>
            <span style={{
              color: '#e0eacc',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {sanitizeText(vm?.name || 'Unknown')}
            </span>
          </div>
          
          {/* Info Box - Green Theme */}
          <div style={{
            backgroundColor: 'rgba(224,234,204,0.05)',
            border: '1px solid rgba(224,234,204,0.2)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#e0eacc" style={{ flexShrink: 0 }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            <span style={{ color: '#e0eacc', fontSize: '12px' }}>
              The VM will boot up and become available.
            </span>
          </div>
          
          {/* Start Button - Green Theme */}
          <button
            onClick={handleConfirm}
            disabled={!canStart || isStarting}
            style={{
              width: '100%',
              backgroundColor: (!canStart || isStarting) ? '#3d4435' : '#e0eacc',
              border: 'none',
              color: (!canStart || isStarting) ? '#888' : '#000',
              borderRadius: '8px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 20px',
              fontSize: '14px',
              cursor: (!canStart || isStarting) ? 'not-allowed' : 'pointer',
              gap: '8px'
            }}
          >
            {isStarting ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTopColor: '#000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Starting...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                Start VM
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
// UPDATED: Stop Confirmation Modal Component with comprehensive logging
const StopConfirmationModal = ({ isOpen, onClose, onConfirm, vm, isStopping }) => {
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (isOpen && vm) {
      logInfo('StopConfirmationModal opened', {
        vmId: vm.id,
        vmName: vm.name,
        vmStatus: vm.status
      });
    }

    return () => {
      if (isOpen && vm) {
        logInfo('StopConfirmationModal closed', {
          vmId: vm.id,
          wasConfirmed: false
        });
      }
    };
  }, [isOpen, vm]);

  const canStop = useMemo(() => {
    if (!vm) return false;
    const result = vm.status === 'RUNNING';
    
    logInfo('Stop capability check', {
      vmId: vm?.id,
      vmName: vm?.name,
      vmStatus: vm?.status,
      canStop: result
    });
    
    return result;
  }, [vm]);

  const handleConfirm = useCallback(() => {
    logUserAction('stop_confirmed', {
      vmId: vm?.id,
      vmName: vm?.name,
      vmStatus: vm?.status
    });
    
    onConfirm();
  }, [onConfirm, vm]);

  const handleClose = useCallback(() => {
    logUserAction('stop_modal_cancelled', {
      vmId: vm?.id,
      vmName: vm?.name
    });
    
    onClose();
  }, [onClose, vm]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#121212',
        border: '1px solid #333333',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #2a2a2a'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Stop Icon (Square) */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#ff7777">
              <rect x="6" y="6" width="12" height="12" rx="1"/>
            </svg>
            <h3 style={{
              fontWeight: '600',
              color: '#fff',
              margin: 0,
              fontSize: '16px'
            }}>
              Stop VM
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isStopping}
            style={{
              padding: '8px',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: isStopping ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <p style={{
            color: '#9ca3af',
            marginBottom: '16px',
            marginTop: 0,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            Are you sure you want to stop this virtual machine?
          </p>
          
          {/* VM Name Box */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '16px'
          }}>
            <span style={{
              color: '#6b7280',
              fontSize: '11px',
              display: 'block',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              VM Name
            </span>
            <span style={{
              color: '#e0eacc',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {sanitizeText(vm?.name || 'Unknown')}
            </span>
          </div>
          
          {/* Warning Box - Amber Theme */}
          <div style={{
            backgroundColor: 'rgba(251,191,36,0.05)',
            border: '1px solid rgba(251,191,36,0.2)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#fbbf24" style={{ flexShrink: 0 }}>
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <span style={{ color: '#fbbf24', fontSize: '12px' }}>
              All running processes will be terminated.
            </span>
          </div>
          
          {/* Stop Button - Red Theme */}
          <button
            onClick={handleConfirm}
            disabled={!canStop || isStopping}
            style={{
              width: '100%',
              backgroundColor: (!canStop || isStopping) ? '#4a3030' : 'rgba(255,100,100,0.15)',
              border: '1px solid rgba(255,119,119,0.4)',
              color: '#ff7777',
              borderRadius: '8px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 20px',
              fontSize: '14px',
              cursor: (!canStop || isStopping) ? 'not-allowed' : 'pointer',
              gap: '8px'
            }}
          >
            {isStopping ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTopColor: '#ff7777',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Stopping...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="6" width="12" height="12" rx="1"/>
                </svg>
                Stop VM
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};// UPDATED: Restore Confirmation Modal Component with comprehensive logging
const RestoreConfirmationModal = ({ isOpen, onClose, onConfirm, vm, isRestoring }) => {
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (isOpen && vm) {
      logInfo('RestoreConfirmationModal opened', {
        vmId: vm.id,
        vmName: vm.name,
        vmStatus: vm.status
      });
    }

    return () => {
      if (isOpen && vm) {
        logInfo('RestoreConfirmationModal closed', {
          vmId: vm.id,
          wasConfirmed: false
        });
      }
    };
  }, [isOpen, vm]);

  const canRestore = useMemo(() => {
    if (!vm) return false;
    const result = vm.status === 'HIBERNATED';
    
    logInfo('Restore capability check', {
      vmId: vm?.id,
      vmName: vm?.name,
      vmStatus: vm?.status,
      canRestore: result
    });
    
    return result;
  }, [vm]);

  const handleConfirm = useCallback(() => {
    logUserAction('restore_confirmed', {
      vmId: vm?.id,
      vmName: vm?.name,
      vmStatus: vm?.status
    });
    
    onConfirm();
  }, [onConfirm, vm]);

  const handleClose = useCallback(() => {
    logUserAction('restore_modal_cancelled', {
      vmId: vm?.id,
      vmName: vm?.name
    });
    
    onClose();
  }, [onClose, vm]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#121212',
        border: '1px solid #333333',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #2a2a2a'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Play/Restore Icon */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#e0eacc">
              <path d="M8 5v14l11-7z"/>
            </svg>
            <h3 style={{
              fontWeight: '600',
              color: '#fff',
              margin: 0,
              fontSize: '16px'
            }}>
              Restore VM
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isRestoring}
            style={{
              padding: '8px',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: isRestoring ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <p style={{
            color: '#9ca3af',
            marginBottom: '16px',
            marginTop: 0,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            Are you sure you want to restore this virtual machine from hibernation?
          </p>
          
          {/* VM Name Box */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '16px'
          }}>
            <span style={{
              color: '#6b7280',
              fontSize: '11px',
              display: 'block',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              VM Name
            </span>
            <span style={{
              color: '#e0eacc',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {sanitizeText(vm?.name || 'Unknown')}
            </span>
          </div>
          
          {/* Info Box - Green Theme */}
          <div style={{
            backgroundColor: 'rgba(224,234,204,0.05)',
            border: '1px solid rgba(224,234,204,0.2)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#e0eacc" style={{ flexShrink: 0 }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            <span style={{ color: '#e0eacc', fontSize: '12px' }}>
              The VM will resume from its hibernated state.
            </span>
          </div>
          
          {/* Restore Button - Green Theme */}
          <button
            onClick={handleConfirm}
            disabled={!canRestore || isRestoring}
            style={{
              width: '100%',
              backgroundColor: (!canRestore || isRestoring) ? '#3d4435' : '#e0eacc',
              border: 'none',
              color: (!canRestore || isRestoring) ? '#888' : '#000',
              borderRadius: '8px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 20px',
              fontSize: '14px',
              cursor: (!canRestore || isRestoring) ? 'not-allowed' : 'pointer',
              gap: '8px'
            }}
          >
            {isRestoring ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTopColor: '#000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Restoring...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                Restore VM
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};const ConfirmationModal = ({ isOpen, onClose, onConfirm, isLoading, vm }) => {
    const { isMobile } = useResponsive();
    const notification = useNotification();
    
    const [error, setError] = useState(null);
    const [lastShownError, setLastShownError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
      if (isOpen) {
        logInfo('ConfirmationModal opened', {
          modalType: 'delete_vm'
        });
      }

      return () => {
        if (isOpen) {
          logInfo('ConfirmationModal closed', {
            modalType: 'delete_vm',
            wasConfirmed: false
          });
        }
      };
    }, [isOpen]);

    useEffect(() => {
        if (error && error !== lastShownError) {
            notification.error(error);
            setLastShownError(error);
            setError(null);
            
            logError('Confirmation modal error displayed', {
              error
            });
        }
    }, [error, notification, lastShownError]);

    useEffect(() => {
        if (successMessage) {
            notification.success(successMessage);
            setSuccessMessage('');
            
            logInfo('Confirmation modal success message displayed', {
              message: successMessage
            });
        }
    }, [successMessage, notification]);

    const handleConfirm = useCallback(() => {
      logUserAction('delete_vm_confirmed', {
        source: 'confirmation_modal'
      });
      
      onConfirm();
    }, [onConfirm]);

    const handleClose = useCallback(() => {
      logUserAction('delete_vm_cancelled', {
        source: 'confirmation_modal'
      });
      
      onClose();
    }, [onClose]);
    
    if (!isOpen) return null;

    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: '16px'
      }}>
        <div style={{
          backgroundColor: '#121212',
          border: '1px solid #333333',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '420px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #2a2a2a'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#ff7777">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
              <h3 style={{
                fontWeight: '600',
                color: '#fff',
                margin: 0,
                fontSize: '16px'
              }}>
                Delete VM
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              style={{
                padding: '8px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#6b7280',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
          
          {/* Content */}
          <div style={{ padding: '20px' }}>
            <p style={{
              color: '#9ca3af',
              marginBottom: '16px',
              marginTop: 0,
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              Are you sure you want to delete this virtual machine?
            </p>
            
            {/* VM Name Box */}
            <div style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #2a2a2a',
              borderRadius: '8px',
              padding: '12px 14px',
              marginBottom: '16px'
            }}>
              <span style={{
                color: '#6b7280',
                fontSize: '11px',
                display: 'block',
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                VM Name
              </span>
              <span style={{
                color: '#e0eacc',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {sanitizeText(vm?.name || 'Unknown')}
              </span>
            </div>
            
            {/* Warning Box */}
            <div style={{
              backgroundColor: 'rgba(255,119,119,0.05)',
              border: '1px solid rgba(255,119,119,0.2)',
              borderRadius: '8px',
              padding: '10px 12px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#ff8080" style={{ flexShrink: 0 }}>
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
              <span style={{ color: '#ff8080', fontSize: '12px' }}>
                This action cannot be undone.
              </span>
            </div>
            
            {/* Delete Button */}
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              style={{
                width: '100%',
                backgroundColor: isLoading ? '#4a3030' : 'rgba(255,100,100,0.15)',
                border: '1px solid rgba(255,119,119,0.4)',
                color: '#ff7777',
                borderRadius: '8px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 20px',
                fontSize: '14px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                gap: '8px'
              }}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTopColor: '#ff7777',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Deleting...
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                  Delete VM
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
};

const VMDetailsSection = ({ 
  selectedVmForDetails, 
  getStatusBadgeClass, 
  getStatusTextColor, 
  calculateVmPricing, 
  navigate,
  isAuthenticated 
}) => {
  const { isMobile } = useResponsive();
  const notification = useNotification();

  // Granular loading states
  const [loadingStates, setLoadingStates] = useState({
      starting: new Set(),
      stopping: new Set(),
      rebooting: new Set()
  });

  // Error states with toast notification
  const [error, setError] = useState(null);
  const [lastShownError, setLastShownError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // ADDED: Component lifecycle logging
  useEffect(() => {
    if (selectedVmForDetails) {
      logInfo('VMDetailsSection rendered', {
        vmId: selectedVmForDetails.id,
        vmName: selectedVmForDetails.name,
        vmStatus: selectedVmForDetails.status,
        isAuthenticated
      });
    }
  }, [selectedVmForDetails, isAuthenticated]);

  // Toast notification conversion
  useEffect(() => {
      if (error && error !== lastShownError) {
          notification.error(error);
          setLastShownError(error);
          setError(null);
          
          logError('VM details section error', {
            error,
            vmId: selectedVmForDetails?.id
          });
      }
  }, [error, notification, lastShownError, selectedVmForDetails]);

  useEffect(() => {
      if (successMessage) {
          notification.success(successMessage);
          setSuccessMessage('');
          
          logInfo('VM details section success', {
            message: successMessage,
            vmId: selectedVmForDetails?.id
          });
      }
  }, [successMessage, notification, selectedVmForDetails]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }, []);

  if (!selectedVmForDetails) {
    if (!isAuthenticated) {
      return (
        <div className={`bg-[#0a0c0a] border rounded-lg mb-4 border-[#1c2b22] shadow-[0_0_10px_rgba(224,234,204,0.2)] ${isMobile ? 'p-4' : 'p-6'}`}>
          <div className="text-center">
            <h3 className={`font-semibold text-white mb-3 ${isMobile ? 'text-base' : 'text-lg'}`}>VM Management</h3>
            <p className={`text-gray-400 mb-4 ${isMobile ? 'text-sm' : 'text-base'}`}>Sign up to deploy and manage virtual machines</p>
            <button
              onClick={() => {
                logUserAction('signup_redirect_clicked', { source: 'vm_details_section' });
                navigate('/signup');
              }}
              className={`bg-[#e0eacc] text-black rounded-full font-medium hover:bg-[#c8d9a9] transition-all hover:translate-y-[-1px] ${isMobile ? 'px-4 py-2 text-sm w-full' : 'px-6 py-2'}`}
            >
              Sign Up to Get Started
            </button>
          </div>
        </div>
      );
    }
    return null;
  }
  
  return (
    <div 
      key={selectedVmForDetails.id}
      className={`bg-[#0a0c0a] border rounded-lg mb-4 border-[#1c2b22] shadow-[0_0_10px_rgba(224,234,204,0.2)] ${isMobile ? 'p-3' : 'p-4'}`}
    >
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center">
          <div className={`w-1 h-8 ${getStatusBadgeClass(selectedVmForDetails.status)} rounded mr-3`}></div>
          <div>
            <div className="flex items-center">
              <h3 className={`font-semibold ${isMobile ? 'text-base' : 'text-lg'}`}>{sanitizeText(selectedVmForDetails.name)}</h3>
              <span className={`ml-3 px-2 py-1 text-xs rounded font-medium border bg-[rgba(202,217,227,0.1)] text-[#cad9e3] border-[rgba(202,217,227,0.3)]`}>
                {sanitizeText(selectedVmForDetails.status)}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{sanitizeText(selectedVmForDetails.specs?.gpu_type)}</p>          </div>
        </div>
      </div>
      
      <div className={`grid text-sm ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`} style={{ marginTop: '0.6cm' }}>
<div><span className="text-gray-400">GPU Count:</span><span className="text-white text-sm">&nbsp;{selectedVmForDetails.specs?.gpu_count || 0}x</span></div>
        <div><span className="text-gray-400">SSH Key:</span><span className="text-white">&nbsp;&nbsp;{sanitizeText(selectedVmForDetails.ssh_key_name)}</span></div>
        <div><span className="text-gray-400">Public IP:</span><span className="text-white text-sm">&nbsp;&nbsp;{sanitizeText(selectedVmForDetails.floating_ip) || 'None'}</span></div>
        <div>
          <span className="text-gray-400">Price:</span>
          <span className="font-medium text-[#cad9e3]">&nbsp;&nbsp;{calculateVmPricing(selectedVmForDetails).symbol}{calculateVmPricing(selectedVmForDetails).hourly.toFixed(2)}/hour</span>
        </div>
<div><span className="text-gray-400">Region:</span><span className="text-white">&nbsp;&nbsp;&nbsp;{(() => {
  const region = selectedVmForDetails.region || 'Unknown';
  const sanitizedRegion = sanitizeText(region);
  return sanitizedRegion.charAt(0).toUpperCase() + sanitizedRegion.slice(1).toLowerCase();
})()}</span></div>
        <div><span className="text-gray-400">Created:</span><span className="text-white text-sm">&nbsp;&nbsp;{formatDate(selectedVmForDetails.created_at)}</span></div>
      </div>
      
      <div className={`flex space-x-2 ${isMobile ? 'flex-col space-y-2 space-x-0 mt-4' : 'justify-end mt-3'}`}>
       
      </div>
    </div>
  );
};

// Continue with remaining components in next response due to length...

// UPDATED: VM Selector Section with comprehensive logging
const VMSelectorSection = ({
  vms,
  selectedVmForDetails,
  vmSelectorDropdownOpen,
  setVmSelectorDropdownOpen,
  handleVmSelect,
  getStatusTextColor,
  navigate,
  isAuthenticated
}) => {
  const { isMobile } = useResponsive();
  const notification = useNotification();

  // Error states with toast notification
  const [error, setError] = useState(null);
  const [lastShownError, setLastShownError] = useState('');

  // ADDED: Component lifecycle logging
  useEffect(() => {
    logInfo('VMSelectorSection rendered', {
      vmCount: vms.length,
      hasSelectedVm: !!selectedVmForDetails,
      isAuthenticated,
      isDropdownOpen: vmSelectorDropdownOpen
    });
  }, [vms.length, selectedVmForDetails, isAuthenticated, vmSelectorDropdownOpen]);

  // Toast notification conversion
  useEffect(() => {
      if (error && error !== lastShownError) {
          notification.error(error);
          setLastShownError(error);
          setError(null);
          
          logError('VM selector section error', {
            error,
            vmCount: vms.length
          });
      }
  }, [error, notification, lastShownError, vms.length]);

  // UPDATED: Handle VM select with logging
  const handleVmSelectWithLogging = useCallback((vm) => {
    logUserAction('vm_selected_from_dropdown', {
      vmId: vm.id,
      vmName: vm.name,
      vmStatus: vm.status,
      previousVmId: selectedVmForDetails?.id
    });
    
    handleVmSelect(vm);
  }, [handleVmSelect, selectedVmForDetails]);

  if (!isAuthenticated) {
    return (
      <div className="relative mb-5">
        <div className={`w-full bg-[#121a15] border px-4 rounded-md flex items-center justify-between text-sm border-[#1c2b22] ${isMobile ? 'py-3' : 'py-2.5'}`}>
          <div className="flex items-center">
            <Circle className="w-3 h-3 mr-2 text-gray-500" />
            <span className="font-medium text-gray-400">Sign up to access VMs</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mb-5">
      <button 
        onClick={() => {
          const newState = !vmSelectorDropdownOpen;
          setVmSelectorDropdownOpen(newState);
          
          logUserAction('vm_selector_dropdown_toggled', {
            opened: newState,
            hasSelectedVm: !!selectedVmForDetails,
            vmCount: vms.length
          });
        }}
        className={`w-full bg-[#121a15] border rounded-lg flex items-center justify-between text-sm focus:outline-none transition-all hover:translate-y-[-1px] ${
          selectedVmForDetails ? 'border-[rgba(202,217,227,0.3)]' : 'border-[rgba(202,217,227,0.3)]'
        } ${isMobile ? 'px-3 py-3' : 'px-4 py-2.5'}`}
      >
        <div className="flex items-center">
          {selectedVmForDetails ? (
            selectedVmForDetails.status === 'RUNNING' ? (
              <Circle className="w-3 h-3 mr-2 text-green-500 fill-green-500" />
            ) : selectedVmForDetails.status === 'HIBERNATED' ? (
              <Moon className="w-3 h-3 mr-2 text-yellow-500" />
            ) : selectedVmForDetails.status === 'SHUTOFF' ? (
              <Power className="w-3 h-3 mr-2 text-red-500" />
            ) : (
              <Circle className="w-3 h-3 mr-2 text-gray-500" />
            )
          ) : (
            <Circle className="w-3 h-3 mr-2 text-gray-500" />
          )}
          <span className="font-medium text-white">{selectedVmForDetails ? sanitizeText(selectedVmForDetails.name) : 'Select VM...'}</span>
          {selectedVmForDetails && (
            <span className={`ml-2 px-2 py-1 text-xs rounded border font-medium bg-[rgba(202,217,227,0.1)] text-[#cad9e3] border-[rgba(202,217,227,0.3)]`}>
              {sanitizeText(selectedVmForDetails.status)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 ml-[8px]">
          {Array.from({ length: 3 }).map((_, i) => (
            <div 
              key={i}
              className="w-1 h-1 bg-[#cad9e3] rounded-full transition-all duration-200 animate-bounce"
              style={{ 
                animationDelay: `${i * 150}ms`,
                animationDuration: '1s'
              }}
            />
          ))}
        </div>
      </button>
      
      {vmSelectorDropdownOpen && (
        <div className="absolute left-0 right-0 mt-1 bg-[#0a0c0a] border border-[#1c2b22] rounded-lg shadow-lg z-10 overflow-hidden">
          <div className="py-1 max-h-64 overflow-y-auto divide-y divide-[#1c2b22]">
            {vms.map((vm) => (
              <button
                key={vm.id}
                className={`w-full text-left text-sm hover:bg-[#121a15] flex items-center justify-between transition-all duration-200 hover:translate-y-[-1px] ${
                  selectedVmForDetails && selectedVmForDetails.id === vm.id ? 'bg-[#121a15]' : ''
                } ${isMobile ? 'px-3 py-3' : 'px-4 py-2.5'}`}
                onClick={() => handleVmSelectWithLogging(vm)}
              >
                <div className="flex items-center">
                  {vm.status === 'RUNNING' ? (
                    <Circle className="w-3 h-3 mr-2 text-green-500 fill-green-500" />
                  ) : vm.status === 'HIBERNATED' ? (
                    <Moon className="w-3 h-3 mr-2 text-yellow-500" />
                  ) : vm.status === 'SHUTOFF' ? (
                    <Power className="w-3 h-3 mr-2 text-red-500" />
                  ) : (
                    <Circle className="w-3 h-3 mr-2 text-gray-500" />
                  )}
                  <span className="font-medium text-white">{sanitizeText(vm.name)}</span>
                </div>
<div className="text-gray-400 text-xs">{sanitizeText(vm.specs?.gpu_type) || 'No GPU'}</div>
              </button>
            ))}
          </div>
          
          <div className={`border-t border-[#1c2b22] ${isMobile ? 'py-2 px-2' : 'py-2 px-3'}`}>
            <button 
              className={`w-full flex items-center justify-center text-white hover:bg-[#121a15] rounded-md transition-all hover:translate-y-[-1px] ${isMobile ? 'py-2' : 'py-2'}`}
              onClick={() => {
                logUserAction('deploy_new_vm_quick_action', {
                  source: 'vm_selector_dropdown'
                });
                navigate('/deploy');
              }}
            >
              <Server className="w-4 h-4 mr-2" />
              <span className="text-sm">Deploy New VM</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// UPDATED: VM Actions Section with comprehensive logging
const VMActionsSection = ({
  selectedVmForDetails,
  handleViewDetails,
  handleHibernateClick,
  handleRestoreClick,
  setShowDeleteModal,
  handleTakeSnapshotClick,
  handleCreateVolumeClick,
  handleStartClick,
  handleStopClick,
  handleRebootClick,
  isAuthenticated,
  isHibernating = false,
  isRestoring = false,
  isDeleting = false,
  isStarting = false,
  isStopping = false,
  isRebooting = false,
  isCreatingSnapshot = false,
  vmActionsBlocked = false
}) => {
  const { isMobile } = useResponsive();
  const notification = useNotification();

  // Granular loading states
  const [loadingStates, setLoadingStates] = useState({
      hibernating: new Set(),
      restoring: new Set(),
      deleting: new Set(),
      starting: new Set(),
      stopping: new Set(),
      rebooting: new Set(),
      creatingSnapshot: new Set()
  });

  // Error states with toast notification
  const [error, setError] = useState(null);
  const [lastShownError, setLastShownError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // ADDED: Component lifecycle logging
  useEffect(() => {
    if (selectedVmForDetails) {
      logInfo('VMActionsSection rendered', {
        vmId: selectedVmForDetails.id,
        vmName: selectedVmForDetails.name,
        vmStatus: selectedVmForDetails.status,
        isAuthenticated,
        vmActionsBlocked,
        anyOperationInProgress: isHibernating || isRestoring || isDeleting || isStarting || isStopping || isRebooting || isCreatingSnapshot
      });
    }
  }, [selectedVmForDetails, isAuthenticated, vmActionsBlocked, isHibernating, isRestoring, isDeleting, isStarting, isStopping, isRebooting, isCreatingSnapshot]);

  // Toast notification conversion
  useEffect(() => {
      if (error && error !== lastShownError) {
          notification.error(error);
          setLastShownError(error);
          setError(null);
          
          logError('VM actions section error', {
            error,
            vmId: selectedVmForDetails?.id
          });
      }
  }, [error, notification, lastShownError, selectedVmForDetails]);

  useEffect(() => {
      if (successMessage) {
          notification.success(successMessage);
          setSuccessMessage('');
          
          logInfo('VM actions section success', {
            message: successMessage,
            vmId: selectedVmForDetails?.id
          });
      }
  }, [successMessage, notification, selectedVmForDetails]);

  if (!isAuthenticated) {
    return (
      <div className={`bg-[#0a0c0a] border border-[#1c2b22] rounded-md mb-4 ${isMobile ? 'p-3' : 'p-4'}`}>
        <h3 className={`font-medium mb-3 text-gray-400 ${isMobile ? 'text-sm' : 'text-base'}`}>VM Management</h3>
        <div className="text-center">
          <p className={`text-gray-400 mb-3 ${isMobile ? 'text-xs' : 'text-sm'}`}>Access VM controls and management features</p>
          <button
            onClick={() => {
              logUserAction('signup_redirect_clicked', { source: 'vm_actions_section' });
              window.location.href = '/signup';
            }}
            className={`bg-[#e0eacc] text-black rounded-md font-medium hover:bg-[#c8d9a9] transition-all hover:translate-y-[-1px] ${isMobile ? 'px-3 py-2 text-sm w-full' : 'px-4 py-2'}`}
          >
            Sign Up for VM Access
          </button>
        </div>
      </div>
    );
  }

  if (!selectedVmForDetails) return null;

  const vmStatus = selectedVmForDetails.status;
  const isAdvanced = isAdvancedRegion(selectedVmForDetails);
  const anyOperationInProgress = isHibernating || isRestoring || isDeleting || isStarting || isStopping || isRebooting || isCreatingSnapshot;

  // Button enable/disable logic based on status and region
  const canHibernate = isAdvanced 
    ? ['RUNNING', 'SHUTOFF'].includes(vmStatus)
    : ['RUNNING', 'SHUTOFF'].includes(vmStatus);
    
  const canDelete = ['RUNNING', 'SHUTOFF', 'HIBERNATED'].includes(vmStatus);
  const canStart = vmStatus === 'SHUTOFF';
  const canStop = vmStatus === 'RUNNING';
  const canReboot = !isAdvanced && vmStatus === 'RUNNING';
  const canRestore = !isAdvanced && vmStatus === 'HIBERNATED';
  const canSnapshot = !isAdvanced && vmStatus === 'RUNNING';

  return (
    <div className={`bg-[#0a0c0a] border border-[#1c2b22] rounded-md mb-4 ${isMobile ? 'p-3' : 'p-4'}`}>
      <h3 className={`font-medium mb-3 text-gray-400 ${isMobile ? 'text-sm' : 'text-base'}`}>VM Configuration</h3>
      <div className={`gap-2 mb-4 ${isMobile ? 'grid grid-cols-1' : 'grid grid-cols-2'}`}>
        <button 
          className={`bg-[#e0eacc] hover:bg-[#c8d9a9] rounded-lg text-sm font-medium flex items-center justify-center transition-all border border-[#1c2b22] text-black hover:translate-y-[-1px] ${isMobile ? 'py-2 px-3 w-full' : 'px-3 py-2'}`}
          onClick={() => {
            logUserAction('create_volume_button_clicked', {
              vmId: selectedVmForDetails.id,
              vmName: selectedVmForDetails.name,
              source: 'vm_actions_section'
            });
            handleCreateVolumeClick();
          }}
          disabled={anyOperationInProgress}
        >
          <Database className="w-4 h-4 mr-2" />
          Create Volume
        </button>
      </div>
      
      <h3 className={`font-medium mb-3 text-gray-400 ${isMobile ? 'text-sm' : 'text-base'}`}>VM Status Control</h3>
      <div className={`grid grid-cols-2 gap-2 mb-4`}>
        <button 
          className={`bg-[#121a15] hover:bg-[#1a2a1a] rounded-lg text-sm flex items-center justify-center transition-all border border-[#1c2b22] text-gray-300 hover:translate-y-[-1px] ${isMobile ? 'py-2 px-3 w-full' : 'px-3 py-2'}`}
          onClick={() => {
            logUserAction('vm_details_button_clicked', {
              vmId: selectedVmForDetails.id,
              vmName: selectedVmForDetails.name,
              source: 'vm_actions_section'
            });
            handleViewDetails(selectedVmForDetails);
          }}
          disabled={anyOperationInProgress}
        >
          <Eye className="w-4 h-4 mr-2" />
          <span>Details</span>
        </button>

        <button
          className={`rounded-lg text-sm flex items-center justify-center transition-all border hover:translate-y-[-1px] ${
            !canHibernate || anyOperationInProgress || vmActionsBlocked
              ? 'bg-[#0f0f0f] border-[#1a1a1a] text-gray-500 cursor-not-allowed opacity-50'
              : 'bg-[#121a15] hover:bg-[#1a2a1a] border-[#1c2b22] text-gray-300'
          } ${isMobile ? 'py-2 px-3 w-full' : 'px-3 py-2'}`}
          onClick={() => {
            if (canHibernate && !anyOperationInProgress && !vmActionsBlocked) {
              logUserAction('vm_hibernate_button_clicked', {
                vmId: selectedVmForDetails.id,
                vmName: selectedVmForDetails.name,
                vmStatus: selectedVmForDetails.status,
                source: 'vm_actions_section'
              });
              handleHibernateClick(selectedVmForDetails);
            }
          }}
          disabled={!canHibernate || anyOperationInProgress || vmActionsBlocked}
        >
          {isHibernating ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-t-white border-r-transparent border-b-white border-l-transparent rounded-full mr-2"></div>
              Hibernating...
            </>
          ) : (
            <>
              <Moon className="w-4 h-4 mr-2" />
              Hibernate
            </>
          )}
        </button>

        <button 
          className={`rounded-lg text-sm flex items-center justify-center transition-all border hover:translate-y-[-1px] ${
            !canDelete || anyOperationInProgress || vmActionsBlocked
              ? 'bg-[#0f0f0f] border-[#1a1a1a] text-gray-500 cursor-not-allowed opacity-50'
              : 'bg-[#121a15] hover:bg-[#1a2a1a] border-[#1c2b22] text-gray-300'
          } ${isMobile ? 'py-2 px-3 w-full' : 'px-3 py-2'}`}
          onClick={() => {
            if (selectedVmForDetails && canDelete && !anyOperationInProgress && !vmActionsBlocked) {
              logUserAction('vm_delete_button_clicked', {
                vmId: selectedVmForDetails.id,
                vmName: selectedVmForDetails.name,
                vmStatus: selectedVmForDetails.status,
                source: 'vm_actions_section'
              });
              setShowDeleteModal(true);
            }
          }}
          disabled={!canDelete || anyOperationInProgress || vmActionsBlocked}
        >
          {isDeleting ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-t-white border-r-transparent border-b-white border-l-transparent rounded-full mr-2"></div>
              Deleting...
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </>
          )}
        </button>

        <button 
          className={`rounded-lg text-sm flex items-center justify-center transition-all border hover:translate-y-[-1px] ${
            !canSnapshot || anyOperationInProgress || vmActionsBlocked
              ? 'bg-[#0f0f0f] border-[#1a1a1a] text-gray-500 cursor-not-allowed opacity-50'
              : 'bg-[#121a15] hover:bg-[#1a2a1a] border-[#1c2b22] text-gray-300'
          } ${isMobile ? 'py-2 px-3 w-full' : 'px-3 py-2'}`}
          onClick={() => {
            if (canSnapshot && !anyOperationInProgress && !vmActionsBlocked) {
              logUserAction('take_snapshot_button_clicked', {
                vmId: selectedVmForDetails.id,
                vmName: selectedVmForDetails.name,
                vmStatus: selectedVmForDetails.status,
                source: 'vm_actions_section'
              });
              handleTakeSnapshotClick();
            }
          }}
          disabled={!canSnapshot || anyOperationInProgress || vmActionsBlocked}
          title={!canSnapshot ? 'Snapshots are only available for running VMs in legacy regions' : 'Create a snapshot of this VM'}
        >
          {isCreatingSnapshot ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-t-white border-r-transparent border-b-white border-l-transparent rounded-full mr-2"></div>
              Creating...
            </>
          ) : (
            <>
              <Camera className="w-4 h-4 mr-2" />
            Take Snapshot
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const VMPricingSection = ({ selectedVmForDetails, calculateVmPricing, isAuthenticated }) => {
  const { isMobile } = useResponsive();

  if (!isAuthenticated) {
    return (
      <div 
        className={`bg-[#0a0c0a] border border-[#1c2b22] rounded-md ${isMobile ? 'p-3' : 'p-4'}`}
        style={{ boxShadow: '0 1px 3px rgba(224, 234, 204, 0.05)' }}
      >
        <p className="text-gray-400 leading-relaxed" style={{ fontSize: isMobile ? '11.4px' : '13.3px' }}>
          Instances in RUNNING and SHUTOFF status will continue to be billed for compute resources. Hibernate to pause billing.
        </p>
        <p className="leading-relaxed mt-2" style={{ fontSize: isMobile ? '11.4px' : '13.3px', color: '#b0b8c0' }}>
          Transitional states are not billed.
        </p>
      </div>
    );
  }

  if (!selectedVmForDetails) return null;

  return (
    <div 
      className={`bg-[#0a0c0a] border border-[#1c2b22] rounded-md ${isMobile ? 'p-3' : 'p-4'}`}
      style={{ boxShadow: '0 1px 3px rgba(224, 234, 204, 0.05)' }}
    >
      <p className="text-gray-400 leading-relaxed" style={{ fontSize: isMobile ? '11.4px' : '13.3px' }}>
        Instances in RUNNING and SHUTOFF status will continue to be billed for compute resources. Hibernate to pause billing.
      </p>
      <p className="leading-relaxed mt-2" style={{ fontSize: isMobile ? '11.4px' : '13.3px', color: '#b0b8c0' }}>
        Transitional states are not billed.
      </p>
    </div>
  );
};

// UPDATED: Account Info Section with comprehensive logging
const AccountInfoSection = ({ 
  userEmail, 
  barrackId, 
  creditBalance, 
  userCurrency, 
  creditBalanceError,
  handleShowCreditsModal, 
  navigate,
  isAuthenticated 
}) => {
  const { isMobile } = useResponsive();
  const notification = useNotification();

  // Error states with toast notification
  const [error, setError] = useState(null);
  const [lastShownError, setLastShownError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // ADDED: Component lifecycle logging
  useEffect(() => {
    if (isAuthenticated) {
      logInfo('AccountInfoSection rendered', {
        userEmail,
        barrackId,
        currency: userCurrency,
        hasLockedBalance: (creditBalance.locked_usd > 0 || creditBalance.locked_inr > 0 || creditBalance.locked_eur > 0)
      });
    }
  }, [isAuthenticated, userEmail, barrackId, userCurrency, creditBalance]);

  // Toast notification conversion
  useEffect(() => {
      if (error && error !== lastShownError) {
          notification.error(error);
          setLastShownError(error);
          setError(null);
          
          logError('Account info section error', {
            error,
            userEmail,
            barrackId
          });
      }
  }, [error, notification, lastShownError, userEmail, barrackId]);

  useEffect(() => {
      if (successMessage) {
          notification.success(successMessage);
          setSuccessMessage('');
          
          logInfo('Account info section success', {
            message: successMessage,
            userEmail
          });
      }
  }, [successMessage, notification, userEmail]);

  // UPDATED: Handle billing navigation with logging
  const handleBillingClick = useCallback(() => {
    logUserAction('billing_navigation_clicked', {
      currentBalance: creditBalance,
      currency: userCurrency,
      source: 'account_info_section'
    });
    
    navigate('/billing');
  }, [creditBalance, userCurrency, navigate]);

  // UPDATED: Handle add credits with logging
  const handleAddCreditsClick = useCallback(() => {
    logUserAction('add_credits_modal_opened', {
      currentBalance: creditBalance,
      currency: userCurrency,
      source: 'account_info_section'
    });
    
    handleShowCreditsModal();
  }, [creditBalance, userCurrency, handleShowCreditsModal]);

  if (!isAuthenticated) {
    return (
      <div className={`bg-[#0a0c0a] rounded-xl border border-[#1c2b22] mb-3 shadow-lg ${isMobile ? 'p-3' : 'p-3'}`}>
        <div className="text-center">
          <h3 className={`text-gray-400 text-xs uppercase tracking-wider mb-3`}>Account Access</h3>
          <p className={`text-white font-medium mb-4 ${isMobile ? 'text-sm' : 'text-sm'}`}>Sign up for full dashboard access</p>
          <div className={`gap-2 ${isMobile ? 'grid grid-cols-1 space-y-2' : 'grid grid-cols-1'}`}>
            <button
              onClick={() => {
                logUserAction('signup_redirect_clicked', { source: 'account_info_section' });
                navigate('/signup');
              }}
              className={`bg-[#e0eacc] hover:bg-[#c8d9a9] text-black rounded-2xl font-medium transition-all flex items-center justify-center hover:translate-y-[-1px] ${isMobile ? 'py-2 px-3 text-sm w-full' : 'py-2 px-3 text-sm'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Sign Up
            </button>
            <button
              onClick={() => {
                logUserAction('login_redirect_clicked', { source: 'account_info_section' });
                navigate('/login');
              }}
              className={`bg-[#1a2a1a] text-[#e0eacc] rounded-lg font-medium transition-all flex items-center justify-center hover:bg-[#1a2a1a]/80 hover:translate-y-[-1px] ${isMobile ? 'py-2 px-3 text-sm w-full' : 'py-2 px-3 text-sm'}`}
            >
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-[#0a0c0a] rounded-xl border border-[#1c2b22] mb-3 shadow-lg ${isMobile ? 'p-3' : 'p-3'}`}>
      {/* Account header with improved styling */}
      <div className="mb-2 flex items-center">
        <div className="bg-[#1a2a1a] w-6 h-6 rounded-full flex items-center justify-center mr-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-[#cad9e3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2v2M7 4.2l1 1.7M17 4.2l-1 1.7M4 7h16v4c0 4-3 8-8 8s-8-4-8-8V7z" />
            <path d="M4 19h16" />
          </svg>
        </div>
        <div>
          <h3 className="text-gray-400 text-xs uppercase tracking-wider">Account</h3>
          <p className={`text-white font-medium truncate ${isMobile ? 'text-sm' : 'text-sm'}`}>{sanitizeText(userEmail) || 'Not Available'}</p>
        </div>
      </div>
      
      <div className={`gap-2 mb-2 ${isMobile ? 'grid grid-cols-1 space-y-2' : 'grid grid-cols-2'}`}>
        <div className="bg-[#121a15] rounded-lg p-2">
          <h4 className="text-gray-400 text-xs mb-1">Barrack ID</h4>
          <p className={`text-white font-mono ${isMobile ? 'text-sm' : 'text-sm'}`}>{sanitizeText(barrackId) || 'Not Available'}</p>
        </div>
        <div className="bg-[#121a15] rounded-lg p-2">
          <h4 className="text-gray-400 text-xs mb-1">Credits ({sanitizeText(userCurrency)})</h4>
          <p className="text-[#e0eacc] font-medium">
            {userCurrency === 'USD' ? `$${creditBalance.usd}` :
             userCurrency === 'EUR' ? `€${creditBalance.eur}` :
             `₹${creditBalance.inr}`}
          </p>
        </div>
      </div>
      
      {/* Locked balances with subtle indicator */}
      {(creditBalance.locked_usd > 0 || creditBalance.locked_inr > 0 || creditBalance.locked_eur > 0) && (
        <div className="bg-[#121a15] rounded-lg p-2 mb-2 border-l-2 border-[#e0eacc]/30">
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-xs flex items-center">
              <span className="mr-1">🔒</span> Locked for Withdrawal
            </span>
            <span className={`text-white ${isMobile ? 'text-sm' : 'text-sm'}`}>
              {userCurrency === 'USD' ? `$${creditBalance.locked_usd}` :
               userCurrency === 'EUR' ? `€${creditBalance.locked_eur}` :
               `₹${creditBalance.locked_inr}`}
            </span>
          </div>
        </div>
      )}
      {creditBalanceError && (
        <div
          onClick={() => navigate(creditBalanceError.redirectTo)}
          style={{
            padding: '10px 12px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            marginBottom: '8px'
          }}
        >
          <AlertTriangle size={14} color="#ef4444" style={{ flexShrink: 0 }} />
          <p style={{ 
            fontSize: '12px', 
            color: '#ef4444', 
            margin: 0, 
            lineHeight: 1.4,
            flex: 1
          }}>
            {creditBalanceError.message}
          </p>
          <ChevronRight size={14} color="#ef4444" style={{ flexShrink: 0 }} />
        </div>
      )}
      {/* Action Buttons with swapped colors */}
      <div className={`gap-2 ${isMobile ? 'grid grid-cols-1 space-y-2' : 'grid grid-cols-2'}`}>
        <button
          className={`bg-[#e0eacc] hover:bg-[#c8d9a9] text-black rounded-lg font-medium transition-all flex items-center justify-center hover:translate-y-[-1px] ${isMobile ? 'py-2 px-3 text-sm w-full' : 'py-2 px-3 text-sm'}`}
          onClick={handleBillingClick}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
            <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
          </svg>
          Billing
        </button>
        <button 
          className={`text-black rounded-2xl font-medium transition-all flex items-center justify-center hover:translate-y-[-1px] ${isMobile ? 'py-2 px-3 text-sm w-full' : 'py-2 px-3 text-sm'}`}
          style={{
            background: 'linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)'
          }}
          onClick={handleAddCreditsClick}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add Credits
        </button>
      </div>
    </div>
  );
};

// UPDATED: Quick Actions Section with comprehensive logging
const QuickActionsSection = ({ navigate, isAuthenticated }) => {
  const { isMobile } = useResponsive();

  // ADDED: Component lifecycle logging
  useEffect(() => {
    logInfo('QuickActionsSection rendered', {
      isAuthenticated
    });
  }, [isAuthenticated]);

  // UPDATED: Quick action handlers with logging
  const handleDeployVM = useCallback(() => {
    logUserAction('quick_action_deploy_vm', {
      source: 'quick_actions_section'
    });
    navigate('/deploy');
  }, [navigate]);

  const handleSSHKeys = useCallback(() => {
    logUserAction('quick_action_ssh_keys', {
      source: 'quick_actions_section',
      isAuthenticated
    });
    isAuthenticated ? navigate('/ssh-keys') : navigate('/signup');
  }, [navigate, isAuthenticated]);

  const handleVolumes = useCallback(() => {
    logUserAction('quick_action_volumes', {
      source: 'quick_actions_section',
      isAuthenticated
    });
    isAuthenticated ? navigate('/volumes') : navigate('/signup');
  }, [navigate, isAuthenticated]);

  const handleSnapshots = useCallback(() => {
    logUserAction('quick_action_snapshots', {
      source: 'quick_actions_section',
      isAuthenticated
    });
    isAuthenticated ? navigate('/snapshots') : navigate('/signup');
  }, [navigate, isAuthenticated]);

  return (
    <div className={`bg-[#0a0c0a] rounded-xl border border-[#1c2b22] mb-3 shadow-lg ${isMobile ? 'p-3' : 'p-3'}`}>
      <h3 className={`text-gray-400 font-medium mb-2 flex items-center ${isMobile ? 'text-xs' : 'text-xs'}`}>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-[#cad9e3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        Quick Actions
      </h3>
      
      <div className={`gap-2 ${isMobile ? 'grid grid-cols-2' : 'grid grid-cols-2'}`}>
        <button 
          className={`flex flex-col items-center justify-center bg-[#121a15] hover:bg-[#1c2b22] rounded-md text-center transition-all border border-[#1c2b22] hover:border-[#e0eacc]/30 group hover:translate-y-[-1px] ${isMobile ? 'p-2' : 'p-2'}`}
          onClick={handleDeployVM}
        >
          <div className={`rounded-full bg-[#1c2b22] flex items-center justify-center mb-1 group-hover:bg-[#cad9e3]/10 transition-colors ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className={`text-[#cad9e3] ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
            </svg>
          </div>
          <span className={`text-gray-300 group-hover:text-[#cad9e3] transition-colors ${isMobile ? 'text-xs' : 'text-xs'}`}>Deploy VM</span>
        </button>
        
        <button 
          className={`flex flex-col items-center justify-center bg-[#121a15] hover:bg-[#1c2b22] rounded-md text-center transition-all border border-[#1c2b22] hover:border-[#e0eacc]/30 group hover:translate-y-[-1px] ${isMobile ? 'p-2' : 'p-2'}`}
          onClick={handleSSHKeys}
        >
          <div className={`rounded-full bg-[#1c2b22] flex items-center justify-center mb-1 group-hover:bg-[#e0eacc]/10 transition-colors ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className={`text-[#cad9e3] ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <span className={`text-gray-300 group-hover:text-[#cad9e3] transition-colors ${isMobile ? 'text-xs' : 'text-xs'}`}>SSH Keys</span>
        </button>
        
        <button 
          className={`flex flex-col items-center justify-center bg-[#121a15] hover:bg-[#1c2b22] rounded-md text-center transition-all border border-[#1c2b22] hover:border-[#e0eacc]/30 group hover:translate-y-[-1px] ${isMobile ? 'p-2' : 'p-2'}`}
          onClick={handleVolumes}
        >
          <div className={`rounded-full bg-[#1c2b22] flex items-center justify-center mb-1 group-hover:bg-[#cad9e3]/10 transition-colors ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className={`text-[#cad9e3] ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </div>
          <span className={`text-gray-300 group-hover:text-[#cad9e3] transition-colors ${isMobile ? 'text-xs' : 'text-xs'}`}>Volumes</span>
        </button>
        
        <button 
          className={`flex flex-col items-center justify-center bg-[#121a15] hover:bg-[#1c2b22] rounded-md text-center transition-all border border-[#1c2b22] hover:border-[#e0eacc]/30 group hover:translate-y-[-1px] ${isMobile ? 'p-2' : 'p-2'}`}
          onClick={handleSnapshots}
        >
          <div className={`rounded-full bg-[#1c2b22] flex items-center justify-center mb-1 group-hover:bg-[#cad9e3]/10 transition-colors ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className={`text-[#cad9e3] ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <span className={`text-gray-300 group-hover:text-[#cad9e3] transition-colors ${isMobile ? 'text-xs' : 'text-xs'}`}>Snapshots</span>
        </button>
      </div>
    </div>
  );
};

// UPDATED: Logout Section with comprehensive logging
const LogoutSection = ({ handleLogout, isAuthenticated }) => {
  const { isMobile } = useResponsive();

  // ADDED: Component lifecycle logging
  useEffect(() => {
    logInfo('LogoutSection rendered', {
      isAuthenticated
    });
  }, [isAuthenticated]);

  // UPDATED: Handle logout with logging
  const handleLogoutClick = useCallback(() => {
    logUserAction('logout_initiated', {
      source: 'dashboard'
    });
    
    handleLogout();
  }, [handleLogout]);

  // UPDATED: Handle signup with logging
  const handleSignupClick = useCallback(() => {
    logUserAction('signup_redirect_clicked', { source: 'logout_section' });
    window.location.href = '/signup';
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="sidebar-section">
        <button 
          onClick={handleSignupClick}
          className={`bg-[#e0eacc] hover:bg-[#c8d9a9] text-black rounded-lg font-medium transition-all flex items-center justify-center hover:translate-y-[-1px] ${isMobile ? 'w-full py-2 px-4 text-sm' : 'w-full py-2 px-4 text-sm'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Sign Up
        </button>
      </div>
    );
  }

  return (
    <div className="sidebar-section">
      <button 
        onClick={handleLogoutClick}
        className={`bg-[#121a15] hover:bg-[#1c2b22] text-gray-300 rounded-lg font-medium transition-all flex items-center justify-center border border-[#1c2b22] hover:border-[#e0eacc]/20 hover:translate-y-[-1px] ${isMobile ? 'w-full py-2 px-4 text-sm' : 'w-1/2 ml-auto py-2 px-4 text-sm'}`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
        Logout
      </button>
    </div>
  );
};

// UPDATED: BarrackVMCard with comprehensive logging
const BarrackVMCard = ({ 
    vmData, 
    number, 
    onDelete, 
    onViewDetails, 
    userPreferredCurrency,
    onHibernate,
    onRestore,
    onCreateVolume
}) => {
    const { isMobile } = useResponsive();
    const navigate = useNavigate();
    const notification = useNotification();
    
    // Granular loading states
    const [loadingStates, setLoadingStates] = useState({
        deleting: new Set(),
        creating: new Set()
    });
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeleteNotification, setShowDeleteNotification] = useState(false);
    const [showHourlyRate, setShowHourlyRate] = useState(false);
    const [showCreateVolumeModal, setShowCreateVolumeModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Error states with toast notification
    const [error, setError] = useState(null);
    const [lastShownError, setLastShownError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // ADDED: Component lifecycle logging
    useEffect(() => {
      logInfo('BarrackVMCard rendered', {
        vmId: vmData.id,
        vmName: vmData.name,
        vmStatus: vmData.status,
        cardNumber: number
      });
    }, [vmData.id, vmData.name, vmData.status, number]);

    // Toast notification conversion
    useEffect(() => {
        if (error && error !== lastShownError) {
            notification.error(error);
            setLastShownError(error);
            setError(null);
            
            logError('VM card error', {
              error,
              vmId: vmData.id,
              vmName: vmData.name
            });
        }
    }, [error, notification, lastShownError, vmData]);

    useEffect(() => {
        if (successMessage) {
            notification.success(successMessage);
            setSuccessMessage('');
            
            logInfo('VM card success', {
              message: successMessage,
              vmId: vmData.id
            });
        }
    }, [successMessage, notification, vmData.id]);

    // Error clearing utility
    const clearError = useCallback(() => {
        setError(null);
        setLastShownError('');
    }, []);

  const calculatePrice = useCallback((specs) => {
    const startTime = performance.now();
    
    if (!specs || !specs.gpu_type) {
      logInfo('VM price calculation skipped - no GPU', {
        vmId: vmData.id,
        vmName: vmData.name
      });
      return { pricePerHour: 0, currency: 'INR' };
    }
    
       const gpuTypeMapping = {
    // Backend naming pattern
    'H100-SXM5-80GB': { price: { INR: 229, USD: 2.69, EUR: 2.89 } },
    'H100-PCIe-80GB': { price: { INR: 175, USD: 1.99, EUR: 2.15 } },
    'H100-PCIe-NVLink-80GB': { price: { INR: 199, USD: 2.39, EUR: 2.49 } },
    'A100-PCIe-80GB': { price: { INR: 125, USD: 1.49, EUR: 1.59 } },
    
    // Frontend naming pattern
    'H100-80G-SXM5': { price: { INR: 229, USD: 2.69, EUR: 2.89 } },
    'H100-80G-PCIe': { price: { INR: 175, USD: 1.99, EUR: 2.15 } },
    'H100-80G-PCIe-NVLink': { price: { INR: 199, USD: 2.39, EUR: 2.49 } },
    'A100-80G-PCIe': { price: { INR: 125, USD: 1.49, EUR: 1.59 } },
    
    // Common/shared naming
    'L40': { price: { INR: 99, USD: 1.19, EUR: 1.25 } },
    'RTX-A6000': { price: { INR: 49, USD: 0.59, EUR: 0.65 } },
    
    // B300, H200 and B200 variants
    'B300-262GB': { price: { INR: 499, USD: 5.89, EUR: 6.15 } },
    'B300': { price: { INR: 499, USD: 5.89, EUR: 6.15 } },
    '1x B300 SXM6 262GB': { price: { INR: 499, USD: 5.89, EUR: 6.15 } },
    'B300 SXM6 262GB': { price: { INR: 499, USD: 5.89, EUR: 6.15 } },
    'H200-141GB': { price: { INR: 299, USD: 3.49, EUR: 3.75 } },
    'H200': { price: { INR: 299, USD: 3.49, EUR: 3.75 } },
    'H200 SXM5 141GB': { price: { INR: 299, USD: 3.49, EUR: 3.75 } },
    '1x H200 SXM5 141GB': { price: { INR: 299, USD: 3.49, EUR: 3.75 } },
    'B200-180GB': { price: { INR: 399, USD: 4.69, EUR: 4.99 } },
    'B200': { price: { INR: 399, USD: 4.69, EUR: 4.99 } },
    '1x B200 180GB': { price: { INR: 399, USD: 4.69, EUR: 4.99 } },
    'B200 180GB': { price: { INR: 399, USD: 4.69, EUR: 4.99 } },
};
        
       const gpuType = specs.gpu_type;
const gpuCount = specs.gpu_count || 1;
        
        // First try direct match
        let gpuInfo = gpuTypeMapping[gpuType];
        
        if (!gpuInfo) {
            const gpuLower = gpuType.toLowerCase();
            
            for (const [key, value] of Object.entries(gpuTypeMapping)) {
                const keyLower = key.toLowerCase();
                
                if (gpuLower.includes(keyLower) || 
                    keyLower.includes(gpuLower) ||
                    (gpuLower.includes('b300') && keyLower.includes('b300')) ||
                    (gpuLower.includes('h200') && keyLower.includes('h200')) ||
                    (gpuLower.includes('b200') && keyLower.includes('b200'))) {
                    gpuInfo = value;
                    break;
                }
            }
        }
        
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (!gpuInfo) {
          logInfo('VM price calculation - GPU type not found', {
            vmId: vmData.id,
            vmName: vmData.name,
            gpuType,
            duration: `${duration.toFixed(2)}ms`
          });
          return { pricePerHour: 0, currency: userPreferredCurrency || 'INR' };
        }
        
        const currency = userPreferredCurrency || 'INR';
        const pricePerHour = (gpuInfo.price[currency] || 0) * gpuCount;
        
        logPerformance('vm_price_calculation', duration, {
          vmId: vmData.id,
          vmName: vmData.name,
          gpuType,
          gpuCount,
          pricePerHour,
          currency
        });
        
        return { pricePerHour, currency };
    }, [userPreferredCurrency, vmData.id, vmData.name]);

const { pricePerHour, currency } = useMemo(() => calculatePrice(vmData.specs), [vmData.specs, calculatePrice]);
    const currencySymbol = currencyUtils.getSymbol(currency);
    const pricePerMinute = pricePerHour / 60;
    
    const togglePriceDisplay = useCallback(() => {
        setShowHourlyRate(!showHourlyRate);
        
        logUserAction('vm_price_display_toggled', {
            vmId: vmData.id,
            vmName: vmData.name,
            showHourlyRate: !showHourlyRate,
            currentPrice: pricePerHour
        });
    }, [showHourlyRate, vmData.id, vmData.name, pricePerHour]);
const handleDelete = useCallback(async () => {
    if (loadingStates.deleting.has(vmData.id)) return;
    
    const startTime = performance.now();
    
    setLoadingStates(prev => ({ 
        ...prev, 
        deleting: new Set([...prev.deleting, vmData.id])
    }));
    setShowDeleteModal(false);
    setShowDeleteNotification(true);
    setIsDeleting(true);
    
    logUserAction('vm_delete_initiated', {
        vmId: vmData.id,
        vmName: vmData.name,
        vmStatus: vmData.status
    });
    
    logInfo('Starting VM deletion', {
        vmId: vmData.id,
        vmName: vmData.name
    });
    
    try {
        const response = await api.deleteVM(vmData.id);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            onDelete(vmData.id);
            setSuccessMessage('VM deleted successfully');
            
            logPerformance('delete_vm', duration, {
                success: true,
                vmId: vmData.id,
                vmName: vmData.name
            });
            
            logInfo('VM deleted successfully', {
                vmId: vmData.id,
                vmName: vmData.name,
                duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vm_delete_success', {
                vmId: vmData.id,
                vmName: vmData.name,
                duration: `${duration.toFixed(2)}ms`
            });
        } else {
            const errorMessage = response.error || 'Failed to delete VM';
            throw new Error(errorMessage);
        }
 } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Authentication error
    if (err.status === 401) {
        setError(ERROR_MESSAGES['SESSION_EXPIRED']);
        
        logError('VM delete failed - authentication error', {
            vmId: vmData.id,
            vmName: vmData.name,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        
        logUserAction('vm_delete_failed', {
            vmId: vmData.id,
            vmName: vmData.name,
            error: 'SESSION_EXPIRED'
        });
        return;
    }
    
    // Network error (no status means request never reached server)
    if (!err.status && err.code) {
        setError(err.error);
        
        logError('Network error in handleDelete', { 
            code: err.code,
            online: navigator.onLine,
            vmId: vmData.id,
            vmName: vmData.name,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        
        logUserAction('vm_delete_failed', {
            vmId: vmData.id,
            vmName: vmData.name,
            error: err.code
        });
        return;
    }
    
    // Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setError(err.error);
        
        logError('VM delete failed - rate limit', {
            vmId: vmData.id,
            vmName: vmData.name,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        
        logUserAction('vm_delete_failed', {
            vmId: vmData.id,
            vmName: vmData.name,
            error: 'RATE_LIMIT_EXCEEDED'
        });
        return;
    }
    
    // Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
        setError(ERROR_MESSAGES[err.code]);
        
        logError('VM delete failed', {
            vmId: vmData.id,
            vmName: vmData.name,
            code: err.code,
            duration: `${duration.toFixed(2)}ms`
        }, err);
        
        logUserAction('vm_delete_failed', {
            vmId: vmData.id,
            vmName: vmData.name,
            error: err.code
        });
        return;
    }
    
    // Fallback
    setError(err.error || 'Failed to delete VM');
    
    logPerformance('delete_vm', duration, {
        success: false,
        error: err.error,
        vmId: vmData.id
    });
    
    logError('VM delete failed', {
        vmId: vmData.id,
        vmName: vmData.name,
        duration: `${duration.toFixed(2)}ms`,
        code: err.code,
        error: err.error
    }, err);
    
    logUserAction('vm_delete_failed', {
        vmId: vmData.id,
        vmName: vmData.name,
        error: err.error
    });
} finally {
    setIsDeleting(false);
    setShowDeleteNotification(false);
    setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(vmData.id);
        return { ...prev, deleting: newDeleting };
    });
}
}, [vmData, loadingStates, onDelete]);  


    const location = vmData.location || "Unknown";
    
    // Check VM status to determine which buttons to show
    const isVmActive = vmData.status === 'RUNNING';
    const isVmHibernated = vmData.status === 'HIBERNATED';
    const isVmStopped = vmData.status === 'SHUTOFF';
    
const gpuDisplayInfo = vmData.specs && vmData.specs.gpu_count > 0 
    ? `${vmData.specs.gpu_count}x ${vmData.specs.gpu_type}`
    : "No GPU";
        
    // Get status badge class based on VM status
    const getStatusBadgeClass = useCallback(() => {
        if (isVmActive) return 'bg-green-500';
        if (isVmHibernated) return 'bg-yellow-500';
        if (isVmStopped) return 'bg-red-500';
        return 'bg-gray-500';
    }, [isVmActive, isVmHibernated, isVmStopped]);

    const handleCreateVolumeClick = useCallback(() => {
        setShowCreateVolumeModal(true);
        
        logUserAction('create_volume_modal_opened', {
            vmId: vmData.id,
            vmName: vmData.name,
            source: 'vm_card'
        });
    }, [vmData.id, vmData.name]);

    const handleVolumeCreated = useCallback((volumeData) => {
        setShowCreateVolumeModal(false);
        if (onCreateVolume) {
            onCreateVolume(volumeData, vmData.id);
        }
        
        logUserAction('volume_created_from_vm_card', {
            vmId: vmData.id,
            vmName: vmData.name,
            volumeId: volumeData.id,
            volumeName: volumeData.name
        });
        
        logInfo('Volume created from VM card', {
            vmId: vmData.id,
            volumeId: volumeData.id
        });
    }, [onCreateVolume, vmData.id, vmData.name]);

    // UPDATED: Handle card click with logging
    const handleCardClick = useCallback(() => {
      logUserAction('vm_card_clicked', {
        vmId: vmData.id,
        vmName: vmData.name,
        vmStatus: vmData.status
      });
      onViewDetails(vmData);
    }, [vmData, onViewDetails]);

    // UPDATED: Handle details button with logging
    const handleDetailsClick = useCallback((e) => {
      e.stopPropagation();
      logUserAction('vm_details_button_clicked', {
        vmId: vmData.id,
        vmName: vmData.name,
        source: 'vm_card'
      });
      onViewDetails(vmData);
    }, [vmData, onViewDetails]);

    // UPDATED: Handle hibernate with logging
    const handleHibernateClick = useCallback((e) => {
      e.stopPropagation();
      logUserAction('vm_hibernate_button_clicked', {
        vmId: vmData.id,
        vmName: vmData.name,
        vmStatus: vmData.status,
        source: 'vm_card'
      });
      onHibernate(vmData);
    }, [vmData, onHibernate]);

    // UPDATED: Handle start with logging
    const handleStartClick = useCallback((e) => {
      e.stopPropagation();
      logUserAction('vm_start_button_clicked', {
        vmId: vmData.id,
        vmName: vmData.name,
        vmStatus: vmData.status,
        source: 'vm_card'
      });
    }, [vmData]);

    // UPDATED: Handle delete modal with logging
    const handleDeleteModalOpen = useCallback((e) => {
      e.stopPropagation();
      logUserAction('vm_delete_modal_opened', {
        vmId: vmData.id,
        vmName: vmData.name,
        source: 'vm_card'
      });
      setShowDeleteModal(true);
    }, [vmData]);

    return (
        <>
          <div 
            className={`bg-gray-900 border rounded-lg mb-4 cursor-pointer transition-all transform hover:translate-y-[-2px] border-gray-800 hover:border-gray-700 ${isMobile ? 'p-3' : 'p-4'}`}
            onClick={handleCardClick}
          >
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center">
                <div className={`w-1 h-8 ${getStatusBadgeClass()} rounded mr-3`}></div>
                <div>
                  <div className="flex items-center">
                    <h3 className={`font-semibold ${isMobile ? 'text-base' : 'text-lg'}`}>{sanitizeText(vmData.name)}</h3>
                    <span className={`ml-3 px-2 py-1 text-xs rounded font-medium border bg-[rgba(202,217,227,0.1)] text-[#cad9e3] border-[rgba(202,217,227,0.3)]`}>
                      {sanitizeText(vmData.status)}
                    </span>
                  </div>
<p className="text-xs text-gray-400 mt-0.5">{sanitizeText(vmData.specs?.gpu_type)}</p>
                </div>
              </div>
            </div>
            
            <div className={`bg-gray-800 rounded-md mb-3 ${isMobile ? 'p-3' : 'p-4'}`}>
              <h4 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Connectivity</h4>
              <div className={`text-sm ${isMobile ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-2 gap-3'}`}>
                <div><span className="text-gray-400">Private IP:</span> <span className="font-mono">{sanitizeText(vmData.fixed_ip)}</span></div>
                <div><span className="text-gray-400">Public IP:</span> <span className="font-mono">{sanitizeText(vmData.floating_ip) || 'None'}</span></div>
                <div><span className="text-gray-400">SSH Key:</span> {sanitizeText(vmData.ssh_key_name)}</div>
                <div><span className="text-gray-400">Location:</span> {sanitizeText(location)}</div>
                <div>
                  <span className="text-gray-400">Volume:</span>
                  {vmData.volume_attachments && vmData.volume_attachments.length > 0 ? (
                    <span className="text-white text-sm"> {sanitizeText(vmData.volume_attachments[0].volume?.name)}</span>
                  ) : (
                    <button 
                      className="text-[#e0eacc] hover:underline text-xs focus:outline-none transition-all hover:translate-y-[-1px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateVolumeClick();
                      }}
                    >CREATE VOLUME</button>
                  )}
                </div>
                <div>
                  <span className="text-gray-400">Price:</span>
                  <span className="font-medium text-[#cad9e3]"> {currencySymbol}{pricePerHour.toFixed(2)}/hour</span>
                </div>
              </div>
            </div>
            
            <div className={`space-x-2 ${isMobile ? 'flex flex-col space-y-2 space-x-0' : 'flex justify-end'}`}>
              <button 
                className={`bg-gray-800 hover:bg-gray-700 text-white rounded text-sm transition-all hover:translate-y-[-1px] ${isMobile ? 'py-2 px-3 w-full' : 'px-3 py-1.5'}`}
                onClick={handleDetailsClick}
              >
                Details
              </button>
              {isVmActive ? (
                <button 
                  className={`bg-gray-800 hover:bg-gray-700 text-white rounded text-sm transition-all hover:translate-y-[-1px] ${isMobile ? 'py-2 px-3 w-full' : 'px-3 py-1.5'}`}
                  onClick={handleHibernateClick}
                >
                  Hibernate
                </button>
              ) : isVmStopped ? (
                <button 
                  className={`bg-gray-800 hover:bg-gray-700 text-white rounded text-sm transition-all hover:translate-y-[-1px] ${isMobile ? 'py-2 px-3 w-full' : 'px-3 py-1.5'}`}
                  onClick={handleStartClick}
                >
                  Start
                </button>
              ) : null}
              <button 
                className={`bg-gray-800 hover:bg-red-900 text-white rounded text-sm transition-all hover:translate-y-[-1px] ${isMobile ? 'py-2 px-3 w-full' : 'px-3 py-1.5'}`}
                onClick={handleDeleteModalOpen}
              >
                Delete
              </button>
            </div>
          </div>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal 
                isOpen={showDeleteModal}
                onClose={() => {
                  setShowDeleteModal(false);
                  logUserAction('vm_delete_modal_cancelled', {
                    vmId: vmData.id,
                    vmName: vmData.name
                  });
                }}
                onConfirm={handleDelete}
                isLoading={isDeleting}
                    vm={vmData}  // ✅ ADD THIS LINE

            />
            
            {/* Delete Notification */}
            {showDeleteNotification && (
                <div className="fixed bottom-4 right-4 bg-[#091423] text-white p-4 rounded-md border border-[#132b49] shadow-lg z-50">
                    <p className="text-sm flex items-center">
                        <span className="animate-spin mr-2">⟳</span>
                        Deleting VM... Please wait.
                    </p>
                </div>
            )}


        </>
    );
};

export {
  SnapshotConfirmationModal,
  HibernateConfirmationModal,
  RebootConfirmationModal,
  StartConfirmationModal,
  StopConfirmationModal,
  RestoreConfirmationModal,
  ConfirmationModal,
  VMDetailsSection,
  VMSelectorSection,
  VMActionsSection,
  VMPricingSection,
  AccountInfoSection,
  QuickActionsSection,
  LogoutSection,
  BarrackVMCard,
};